/**
 * Locale — client state (DESIGN.md / CONTEXT.md split: UI-local state lives in
 * Pinia). Wraps the vue-i18n global locale so components can read/set it
 * reactively through the usual store pattern, and keeps <html lang> + the
 * localStorage choice in sync.
 *
 * Source of truth: when logged in, the account's stored locale wins (synced
 * here by auth.fetchUser() via applyAccountLocale). Switching the language
 * while logged in applies immediately and persists back to the account
 * (setLocale); while logged out, a switch only ever touches the UI +
 * localStorage.
 */
import { defineStore } from "pinia";
import { toast } from "vue-sonner";
import { i18n, persistLocale, SUPPORTED_LOCALES, type Locale } from "@/i18n";
import { identityServiceUpdateProfile } from "@/api/identity/identity";
import { translateApiError } from "@/lib/errorMessages";

export const useLocaleStore = defineStore("locale", {
  state: () => ({
    locale: i18n.global.locale.value as Locale,
  }),

  actions: {
    /** UI-only: reactive state + vue-i18n global + <html lang> + localStorage. */
    applyUi(locale: Locale) {
      this.locale = locale;
      i18n.global.locale.value = locale;
      persistLocale(locale);
    },

    /**
     * Called by the auth store's fetchUser() after every login/hydrate: the
     * stored account locale wins over the browser default and any stale
     * localStorage choice left by another person on this device. UI only —
     * this value came from the backend, so there's nothing to persist back.
     */
    applyAccountLocale(locale: string | undefined) {
      if (locale && SUPPORTED_LOCALES.includes(locale as Locale)) {
        this.applyUi(locale as Locale);
      }
    },

    /**
     * User-initiated switch (NavUser, AccountView, AuthLayout). Applies
     * immediately; when logged in, also persists to the account via the same
     * UpdateProfile call AccountView's form uses (first/last name included,
     * since the endpoint updates the whole profile).
     */
    async setLocale(locale: Locale) {
      this.applyUi(locale);

      // Lazy import: stores/auth.ts imports this store to sync the account
      // locale after login/hydrate, so a static import here would cycle.
      const { useAuthStore } = await import("@/stores/auth");
      const auth = useAuthStore();
      if (!auth.isLoggedIn || !auth.user?.id) return;

      try {
        const updated = await identityServiceUpdateProfile({
          first_name: auth.user.first_name,
          last_name: auth.user.last_name,
          locale,
        });
        auth.user.locale = updated.locale ?? locale;
      } catch (error) {
        toast.error(i18n.global.t("account.failedTitle"), {
          description: translateApiError(error),
          duration: 3000,
        });
      }
    },
  },
});
